import { ErrorOutline, PublishedWithChanges } from "@mui/icons-material";
import {
    Chip,
    CircularProgress,
    Grid,
    Stack,
    Typography,
} from "@mui/material";
import MessageStore from "components/ManagerInteractions/Stores/MessageStore";
import SignalsPageStore from "components/Signals/Store/SignalsPageStore";
import AcxButton from "components/UI/AcxButton";
import AcxDialog from "components/UI/Dialog/AcxDialog";
import AcxSelectSingle from "components/UI/Select/BaseSelectComponents/AcxSelectSingle";
import ClassifierGroup from "models/ClassifierGroup";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import ClassifierService, {
    ClassifierInGroup,
    SetClassifierGroupsResponse,
} from "services/ClassifierService";
import theme from "Theme/AppTheme";
import { useStore } from "utils/useStore";
import { getAncestorPath } from "./classifierGroupTree";

interface ManageGroupClassifiersDialogProps {
    isOpen: boolean;
    group: ClassifierGroup;
    groups: ClassifierGroup[];
    byId: Map<string, ClassifierGroup>;
    mode: "manage" | "resolveDelete";
    onClose(): void;
    onApplied(response: SetClassifierGroupsResponse): void | Promise<void>;
}

interface DestinationOption {
    id: string;
    name: string;
    targetGroupId: string | null;
}

const KEEP_DESTINATION_ID = "__keep__";
const UNASSIGN_DESTINATION_ID = "__unassign__";

export default function ManageGroupClassifiersDialog(
    props: ManageGroupClassifiersDialogProps,
) {
    const service = useMemo(() => new ClassifierService(), []);
    const messageStore = useStore(MessageStore);
    const signalsPageStore = useStore(SignalsPageStore);

    const [classifiers, setClassifiers] = useState<ClassifierInGroup[]>([]);
    const [destinations, setDestinations] = useState<Record<string, string>>(
        {},
    );
    const [isLoading, setIsLoading] = useState(false);
    const [isApplying, setIsApplying] = useState(false);
    const [summary, setSummary] = useState<string[]>([]);

    const getGroupPathLabel = useCallback(
        (groupId: string) =>
            getAncestorPath(props.byId, groupId)
                .map((id) => props.byId.get(id)?.name)
                .filter(Boolean)
                .join(" / "),
        [props.byId],
    );

    const destinationOptions = useMemo<DestinationOption[]>(
        () => [
            {
                id: KEEP_DESTINATION_ID,
                name: "Keep",
                targetGroupId: props.group.id,
            },
            ...props.groups
                .filter((group) => group.id !== props.group.id)
                .slice()
                .sort((a, b) =>
                    getGroupPathLabel(a.id).localeCompare(
                        getGroupPathLabel(b.id),
                    ),
                )
                .map((group) => ({
                    id: group.id,
                    name: getGroupPathLabel(group.id),
                    targetGroupId: group.id,
                })),
            {
                id: UNASSIGN_DESTINATION_ID,
                name: "Unassign",
                targetGroupId: null,
            },
        ],
        [getGroupPathLabel, props.group.id, props.groups],
    );

    const loadClassifiers = useCallback(
        async (signal?: AbortSignal) => {
            try {
                setIsLoading(true);
                const result = await service.getClassifiersInGroup(
                    props.group.id,
                    signal,
                );
                setClassifiers(result);
                setDestinations(
                    Object.fromEntries(
                        result.map((classifier) => [
                            classifier.id,
                            KEEP_DESTINATION_ID,
                        ]),
                    ),
                );
                setSummary([]);
            } catch (error) {
                if (error instanceof Error) {
                    messageStore.logError(
                        "Failed to load classifiers: " + error.message,
                    );
                }
            } finally {
                setIsLoading(false);
            }
        },
        [messageStore, props.group.id, service],
    );

    useEffect(() => {
        if (!props.isOpen) return;
        const abortController = new AbortController();
        void loadClassifiers(abortController.signal);
        return () => abortController.abort();
    }, [loadClassifiers, props.isOpen]);

    const resolveTargetGroupId = useCallback(
        (classifierId: string) => {
            const destinationId =
                destinations[classifierId] ?? KEEP_DESTINATION_ID;
            return (
                destinationOptions.find((option) => option.id === destinationId)
                    ?.targetGroupId ?? null
            );
        },
        [destinationOptions, destinations],
    );

    const changedClassifiers = useMemo(
        () =>
            classifiers.filter(
                (classifier) =>
                    resolveTargetGroupId(classifier.id) !== props.group.id,
            ),
        [classifiers, props.group.id, resolveTargetGroupId],
    );

    const setAllDestinations = (destinationId: string) => {
        setDestinations(
            Object.fromEntries(
                classifiers.map((classifier) => [classifier.id, destinationId]),
            ),
        );
        setSummary([]);
    };

    const parentDestination = props.group.parentGroupId
        ? destinationOptions.find(
              (option) => option.id === props.group.parentGroupId,
          )
        : undefined;
    const groupDestinationOptions = destinationOptions.filter(
        (option) =>
            option.id !== KEEP_DESTINATION_ID &&
            option.id !== UNASSIGN_DESTINATION_ID,
    );

    const getDestinationDetail = (
        classifier: ClassifierInGroup,
        destination: DestinationOption,
    ) => {
        if (!classifier.hasModelResultsChart) {
            if (destination.id === KEEP_DESTINATION_ID) {
                return "No group change; no results chart.";
            }

            return `Moves to ${destination.name}; no results chart will be created.`;
        }

        const targetGroupId = destination.targetGroupId;
        if (!targetGroupId) {
            return "Moves to \"Model Results\" as a single chart.";
        }

        const targetGroup = props.byId.get(targetGroupId);
        if (!targetGroup) {
            return "Moves to the selected group.";
        }

        if (targetGroup.parentGroupId) {
            const parent = props.byId.get(targetGroup.parentGroupId);
            return `Moves to "${parent?.name ?? "Parent"} Results" in the "${targetGroup.name}" grouped chart.`;
        }

        return `Moves to "${targetGroup.name} Results" as a single chart.`;
    };

    const apply = async () => {
        const assignments = changedClassifiers.map((classifier) => ({
            classifierId: classifier.id,
            targetGroupId: resolveTargetGroupId(classifier.id),
        }));

        if (assignments.length === 0) return;

        try {
            setIsApplying(true);
            const response = await service.setClassifierGroups(assignments);
            signalsPageStore.invalidatePublishedModelResultsCharts();
            await signalsPageStore.reloadDashboardsById([
                ...new Set(
                    response.results.flatMap(
                        (result) => result.reconcile.affectedDashboardIds,
                    ),
                ),
            ]);

            const resultSummary = response.results.map((result) => {
                const dashboard =
                    result.reconcile.dashboardTitle ?? "no dashboard";
                return `${result.classifierName} -> ${dashboard}`;
            });
            setSummary(resultSummary);

            if (response.results.length > 0) {
                messageStore.logMessage("Classifiers updated", "success");
            }
            response.failures.forEach((failure) =>
                messageStore.logError(
                    `${failure.classifierName ?? failure.classifierId}: ${failure.message}`,
                ),
            );

            await props.onApplied(response);
            await loadClassifiers();
        } catch (error) {
            if (error instanceof Error) {
                messageStore.logError(
                    "Failed to update classifiers: " + error.message,
                );
            }
        } finally {
            setIsApplying(false);
        }
    };

    return (
        <AcxDialog
            isOpen={props.isOpen}
            onClose={props.onClose}
            title={
                props.mode === "resolveDelete"
                    ? `Resolve classifiers in "${props.group.name}"`
                    : `Manage classifiers in "${props.group.name}"`
            }
            contentWidth="760px"
            maxWidth="lg"
            dialogContentChildren={
                <Grid container direction="column" gap={2}>
                    <Stack
                        direction="row"
                        gap={1}
                        flexWrap="wrap"
                        alignItems="flex-end"
                    >
                        <Stack minWidth="260px">
                            <AcxSelectSingle
                                id="move-all-to-group"
                                inputLabel="Move All To"
                                valueField="id"
                                labelField="name"
                                options={groupDestinationOptions}
                                onChange={(option?: DestinationOption) => {
                                    if (option) setAllDestinations(option.id);
                                }}
                                fullWidth
                                isDisabled={
                                    classifiers.length === 0 ||
                                    isLoading ||
                                    isApplying
                                }
                            />
                        </Stack>
                        <AcxButton
                            disabled={
                                classifiers.length === 0 ||
                                isLoading ||
                                isApplying
                            }
                            onClick={() =>
                                setAllDestinations(UNASSIGN_DESTINATION_ID)
                            }
                        >
                            Unassign All
                        </AcxButton>
                        {parentDestination && (
                            <AcxButton
                                disabled={
                                    classifiers.length === 0 ||
                                    isLoading ||
                                    isApplying
                                }
                                onClick={() =>
                                    setAllDestinations(parentDestination.id)
                                }
                            >
                                Move All To Parent (
                                {props.byId.get(props.group.parentGroupId ?? "")
                                    ?.name ?? "Parent"}
                                )
                            </AcxButton>
                        )}
                    </Stack>

                    {isLoading ? (
                        <Stack alignItems="center" padding={3}>
                            <CircularProgress size={28} />
                        </Stack>
                    ) : classifiers.length === 0 ? (
                        <Typography color={theme.palette.text.secondary}>
                            No classifiers are assigned to this group.
                        </Typography>
                    ) : (
                        <Grid container direction="column">
                            <Grid
                                container
                                item
                                columnSpacing={2}
                                paddingBottom={0.75}
                                sx={{
                                    borderBottom: `1px solid ${theme.palette.divider}`,
                                }}
                            >
                                <Grid item xs={12} md={6}>
                                    <Typography
                                        fontWeight={
                                            theme.typography.fontWeightBold
                                        }
                                    >
                                        Model
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Typography
                                        fontWeight={
                                            theme.typography.fontWeightBold
                                        }
                                    >
                                        Destination
                                    </Typography>
                                </Grid>
                            </Grid>
                            {classifiers.map((classifier) => {
                                const selected =
                                    destinationOptions.find(
                                        (option) =>
                                            option.id ===
                                            (destinations[classifier.id] ??
                                                KEEP_DESTINATION_ID),
                                    ) ?? destinationOptions[0];

                                return (
                                    <Grid
                                        key={classifier.id}
                                        container
                                        item
                                        alignItems="flex-start"
                                        columnSpacing={2}
                                        rowSpacing={1}
                                        paddingY={1.25}
                                        sx={{
                                            borderBottom: `1px solid ${theme.palette.divider}`,
                                        }}
                                    >
                                        <Grid item xs={12} md={6}>
                                            <Stack
                                                direction="row"
                                                alignItems="flex-start"
                                                gap={1}
                                                flexWrap="wrap"
                                            >
                                                <Typography
                                                    sx={{
                                                        overflowWrap:
                                                            "anywhere",
                                                    }}
                                                >
                                                    {classifier.name}
                                                </Typography>
                                                {classifier.hasModelResultsChart ? (
                                                    <Chip
                                                        size="small"
                                                        icon={
                                                            <PublishedWithChanges />
                                                        }
                                                        label="Published"
                                                    />
                                                ) : (
                                                    <Chip
                                                        size="small"
                                                        icon={<ErrorOutline />}
                                                        label={
                                                            classifier.isPublished
                                                                ? "No results chart"
                                                                : "Unpublished"
                                                        }
                                                    />
                                                )}
                                            </Stack>
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <AcxSelectSingle
                                                id={`classifier-destination-${classifier.id}`}
                                                valueField="id"
                                                labelField="name"
                                                options={destinationOptions}
                                                defaultValue={selected}
                                                onChange={(
                                                    option?: DestinationOption,
                                                ) => {
                                                    setDestinations(
                                                        (current) => ({
                                                            ...current,
                                                            [classifier.id]:
                                                                option?.id ??
                                                                KEEP_DESTINATION_ID,
                                                        }),
                                                    );
                                                    setSummary([]);
                                                }}
                                                fullWidth
                                                enableNoSelection={false}
                                                isDisabled={isApplying}
                                            />
                                            <Typography
                                                color={
                                                    theme.palette.text.secondary
                                                }
                                                sx={{
                                                    fontSize:
                                                        theme.typography.body2
                                                            .fontSize,
                                                    marginTop: 0.5,
                                                    lineHeight: 1.35,
                                                }}
                                            >
                                                {getDestinationDetail(
                                                    classifier,
                                                    selected,
                                                )}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                );
                            })}
                        </Grid>
                    )}

                    {changedClassifiers.length > 0 && (
                        <Typography color={theme.palette.text.secondary}>
                            Source group charts update automatically.
                        </Typography>
                    )}

                    {summary.length > 0 && (
                        <Grid container direction="column" gap={0.5}>
                            {summary.map((line) => (
                                <Typography key={line}>{line}</Typography>
                            ))}
                        </Grid>
                    )}
                </Grid>
            }
        >
            <AcxButton
                color="primary"
                loading={isApplying}
                disabled={changedClassifiers.length === 0 || isLoading}
                onClick={() => void apply()}
            >
                Apply
            </AcxButton>
        </AcxDialog>
    );
}
